import {useDropzone, ErrorCode} from 'react-dropzone'
import {observer} from 'mobx-react-lite'
import {Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {SnackHandlerInstance} from '@oc/shell-core'
import {IconByKey} from '@oc/icons'
import {OcButton} from '../BasicOcComponents/OcButton'
import {messages} from '../translations'

const useStyles = makeOcStyles(theme => ({
    centered: {
        alignItems: 'center',
        justifyContent: 'center'
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column'
    },
    button: {
        minWidth: '136px'
    },
    dropZone: {
        border: `1px solid ${theme.syskronPalette.colors.P700}`,
        flexGrow: 1
    },
    margin12: {
        margin: '12px'
    },
    selectedFileTypo: {
        marginBottom: '12px'
    },
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24
    },
    footer: {
        width: '100%',
        borderTop: `1px solid ${theme.syskronPalette.colors.S200}`,
        background: theme.syskronPalette.colors.S100,
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        color: theme.syskronPalette.colors.S700
    },
    helper: {
        flex: '1 1 100%'
    },
    errorMessage: {
        color: theme.syskronPalette.colors.W900,
        paddingLeft: '12px'
    },
    dropZoneError: {
        border: `1px solid ${theme.syskronPalette.colors.W900}`
    }
}))

function getFileTypeSuffix(fileName: string) {
    return fileName?.substring(fileName.lastIndexOf('.') + 1)
}

export function getFileSizeLabelWithUnit(maxSizeInBytes: number) {
    const part = 1024

    switch (true) {
        case maxSizeInBytes > Math.pow(part, 3):
            return `${maxSizeInBytes / Math.pow(part, 3)} GB`
        case maxSizeInBytes > Math.pow(part, 2):
            return `${maxSizeInBytes / Math.pow(part, 2)} MB`
        case maxSizeInBytes > part:
            return `${maxSizeInBytes / part} KB`
        default:
            return `${maxSizeInBytes} B`
    }
}

interface DropzoneProps {
    file?: File
    setFile: (file: File | undefined) => void
    allowedFileTypes?: string[]
    maxSizeInBytes?: number
    disabled?: boolean
    helperText?: string
    errorMessage?: string
    dropZoneMessage?: string
}

function _Dropzone({
    file,
    setFile,
    allowedFileTypes,
    maxSizeInBytes,
    disabled,
    helperText,
    dropZoneMessage,
    errorMessage
}: DropzoneProps) {
    const {classes, cx} = useStyles()
    const {getRootProps, getInputProps, open} = useDropzone({
        // Disable click and keydown behavior
        disabled: disabled ?? false,
        noClick: true,
        noKeyboard: true,
        accept: allowedFileTypes?.length
            ? Object.fromEntries(allowedFileTypes.map(fileType => [`.${fileType}`, []]))
            : undefined,
        maxFiles: 1,
        onDropAccepted(files) {
            if (files.length) {
                setFile(files[0])
            }
        },
        onDropRejected: files => {
            if (files.length > 1) {
                SnackHandlerInstance.warning(messages.getText('errors', 'tooManyFiles'))
            } else if (files.length === 1 && files[0].errors.find(error => error.code === ErrorCode.FileInvalidType)) {
                SnackHandlerInstance.warning(messages.getText('errors', 'wrongFileType', allowedFileTypes?.join(' ,')))
            } else if (files.length === 1 && files[0].errors.find(error => error.code === ErrorCode.FileTooLarge)) {
                SnackHandlerInstance.warning(
                    messages.getText('errors', 'fileTooLarge', getFileSizeLabelWithUnit(maxSizeInBytes || 0))
                )
            } else {
                SnackHandlerInstance.warning(
                    files.map(file => file.errors.map(error => error.message).join(',')).join(';')
                )
            }
        },
        validator: file => {
            const fileType = getFileTypeSuffix(file.name)
            if (allowedFileTypes && allowedFileTypes.length > 0 && !allowedFileTypes.includes(fileType)) {
                return {
                    code: ErrorCode.FileInvalidType,
                    message: ''
                }
            }

            if (maxSizeInBytes && file.size > maxSizeInBytes) {
                return {
                    code: ErrorCode.FileTooLarge,
                    message: ''
                }
            }
            return null
        }
    })

    return (
        <>
            <div
                {...getRootProps({className: 'dropzone'})}
                className={cx(
                    classes.flexColumn,
                    classes.centered,
                    classes.dropZone,
                    errorMessage && classes.dropZoneError
                )}
            >
                <input {...getInputProps()} />
                <div className={classes.content}>
                    {!disabled && (
                        <Typography>{dropZoneMessage || messages.getText('uploadDialog', 'dragAndDrop')}</Typography>
                    )}
                    {/* // TODO change to right iconKey */}
                    <IconByKey iconKey="file" size="40px" className={classes.margin12} />
                    {file ? (
                        <Typography className={classes.selectedFileTypo}>
                            {messages.getText('uploadDialog', 'selectedFile')}
                            <strong>{file.name}</strong>
                        </Typography>
                    ) : null}
                    <OcButton className={classes.button} disabled={disabled} onClick={open}>
                        {messages.getText('uploadDialog', 'clickToBrowse')}
                    </OcButton>
                </div>
                {!disabled && (
                    <div className={classes.footer}>
                        {allowedFileTypes && (
                            <Typography variant="caption">
                                {messages.getText(
                                    'misc',
                                    'supportedFormats',
                                    allowedFileTypes.length === 1
                                        ? allowedFileTypes[0].toUpperCase()
                                        : allowedFileTypes.map(type => type.toUpperCase())
                                )}
                            </Typography>
                        )}
                        {maxSizeInBytes && (
                            <Typography variant="caption">
                                {messages.getText('misc', 'maxFileSize', getFileSizeLabelWithUnit(maxSizeInBytes || 0))}
                            </Typography>
                        )}
                    </div>
                )}
            </div>
            {errorMessage && (
                <Typography className={classes.errorMessage} variant="caption">
                    {errorMessage}
                </Typography>
            )}
            <Typography className={classes.helper} color={'textPrimary'} variant="caption">
                {helperText || '\xA0'}
            </Typography>
        </>
    )
}

export const OcDropzone = observer(_Dropzone)
